import { useCallback, useEffect, useState } from 'react'
import { api, can, shortDate, type Persona, type PersonaMapping, type PersonaMappingPreview } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Picker, Status, Toggletip } from '../../components/kit'
import '../../styles/sweep.css'

// Which persona each of your directory's roles or groups signs people in as.
//
// A directory names jobs in its own words: an app role value such as
// TPRM.RiskOfficer, or a group's object id. A word with no row here lets a
// person sign in and do nothing, which is deliberate: nobody is given access by
// default. Words that are already this product's role codes keep working.
//
// One account, one persona: each value gives one persona, and a person whose
// values give two is refused at sign-in with both values named, so the
// directory administrator knows which membership to take away. The API refuses
// mapping a value to a second persona for the same reason.
//
// The screen says the first of those in one line and keeps the rest behind the
// (i) beside it, so the table of mappings is what the eye lands on.

export function PersonaMappings() {
  const [mappings, setMappings] = useState<PersonaMapping[] | null>(null)
  const [personas, setPersonas] = useState<Persona[]>([])
  const [error, setError] = useState<unknown>(null)
  const [value, setValue] = useState('')
  const [persona, setPersona] = useState('')
  const [note, setNote] = useState('')
  // When the mapping stops naming its persona (release 3.1). The external
  // auditor's always does: 30 days when left blank, at most 180.
  const [expires, setExpires] = useState('')
  const [now, setNow] = useState('')
  const [busy, setBusy] = useState('')
  const [problem, setProblem] = useState('')
  const [tryValues, setTryValues] = useState('')
  const [preview, setPreview] = useState<PersonaMappingPreview | null>(null)
  const mayWrite = can('user.administer')

  const load = useCallback(() => {
    api
      .personaMappings()
      .then((r) => {
        setMappings(r.mappings)
        setNow(r.now ?? new Date().toISOString())
        setPersonas(r.personas.filter((p) => p.role))
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])

  if (error) return <ErrorBox error={error} what="Directory roles" />
  if (!mappings) return <Loading what="directory roles" />

  const nameOf = (code: string) => personas.find((p) => p.code === code)?.name ?? code

  return (
    <section className="panel p1-section" aria-labelledby="persona-mappings-h" style={{ marginTop: 16 }}>
      <h2 id="persona-mappings-h">Directory roles to personas</h2>
      <p className="muted sw-row" style={{ margin: '0 0 12px' }}>
        People signing in through your directory arrive as the persona chosen here
        <Toggletip label="How a persona is chosen">
          Anything not listed signs in with no access. Each value gives one persona. Somebody whose values give two
          personas is refused at sign-in.
        </Toggletip>
      </p>

      {mappings.length === 0 ? (
        <p className="note warn sw-row">
          <Status tone="warn">Nothing mapped</Status>
          People signing in through your directory can do nothing yet
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Your directory sends</th>
              <th scope="col">Signs in as</th>
              <th scope="col">Access ends</th>
              <th scope="col">Note</th>
              {mayWrite && <th scope="col">Action</th>}
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => (
              <tr key={m.id}>
                <td>
                  <code>{m.claimValue}</code>
                </td>
                <td>{nameOf(m.persona)}</td>
                <td>
                  {m.expiresAt ? (
                    m.expiresAt <= now ? (
                      <Status tone="crit" title="Its time box has passed, so these values sign nobody in. Add the mapping again with a new date to renew it.">
                        {'Ended ' + shortDate(m.expiresAt)}
                      </Status>
                    ) : (
                      <Status tone="warn">{'Ends ' + shortDate(m.expiresAt)}</Status>
                    )
                  ) : (
                    <span className="muted">No end</span>
                  )}
                </td>
                <td className="muted">{m.note || ''}</td>
                {mayWrite && (
                  <td>
                    <button
                      type="button"
                      className="linkish"
                      disabled={busy !== ''}
                      onClick={async () => {
                        setBusy('remove-' + m.id)
                        try {
                          await api.removePersonaMapping(m.id)
                          load()
                        } finally {
                          setBusy('')
                        }
                      }}
                      data-action="tprm.settings-sso.a08"
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayWrite && (
        <form
          className="p1-actions"
          style={{ marginTop: 16, alignItems: 'flex-end' }}
          onSubmit={async (e) => {
            e.preventDefault()
            setProblem('')
            setBusy('add')
            try {
              await api.addPersonaMapping(value.trim(), persona, note, expires)
              setValue('')
              setNote('')
              setExpires('')
              load()
            } catch (err) {
              // An expiry refused (in the past, or beyond an external
              // auditor's 180 days) comes back as an issue on the field.
              const issues = (err as { issues?: { detail: string }[] }).issues ?? []
              setProblem(
                issues.length > 0
                  ? issues.map((i) => i.detail).join(' ')
                  : err instanceof Error
                    ? err.message
                    : 'That could not be saved.',
              )
            } finally {
              setBusy('')
            }
          }}
        >
          <label className="field">
            <span>Role or group your directory sends</span>
            <input id="pm-value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="TPRM.RiskOfficer" required />
          </label>
          {/* Not inside its label, as the other two fields are: a press
              anywhere in a label is also a press on the first control in it,
              so a press on a row of the list would press the picker again. */}
          <div className="field">
            <label htmlFor="pm-persona">Persona</label>
            <Picker
              bare
              id="pm-persona"
              label="Persona"
              value={persona}
              required
              options={personas.map((p) => ({ value: p.code, label: p.name }))}
              placeholder="Choose: type to search"
              noun="persona"
              onChange={setPersona}
            />
          </div>
          <label className="field">
            <span>
              Access ends{persona === 'external_auditor' ? '' : ' (optional)'}{' '}
              <Toggletip label="When a mapping ends">
                On the date the values stop signing anybody in as this persona. An external auditor is always
                time-boxed: 30 days when left blank, at most 180. Adding an ended mapping again renews it.
              </Toggletip>
            </span>
            <input id="pm-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </label>
          <label className="field">
            <span>Note (optional)</span>
            <input id="pm-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button
            type="submit"
            className="button primary"
            disabled={busy !== '' || !value.trim() || !persona}
            data-action="tprm.settings-sso.a09"
          >
            {busy === 'add' ? 'Adding' : 'Add'}
          </button>
        </form>
      )}
      {problem && (
        <p className="note warn" role="alert">
          {problem}
        </p>
      )}

      {mayWrite && (
        <div style={{ marginTop: 20 }}>
          <label className="field">
            <span>Check a sign-in: the roles or groups one person would send, separated by commas</span>
            <input id="pm-try" value={tryValues} onChange={(e) => setTryValues(e.target.value)} />
          </label>
          <button
            type="button"
            className="button"
            disabled={busy !== '' || !tryValues.trim()}
            onClick={async () => {
              setBusy('preview')
              try {
                setPreview(
                  await api.previewPersonaMapping(
                    tryValues
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean),
                  ),
                )
              } finally {
                setBusy('')
              }
            }}
            data-action="tprm.settings-sso.a10"
          >
            {busy === 'preview' ? 'Checking' : 'Check'}
          </button>
          {/* The answer as a label first, then the sentence: the API's own
              when it refused or found nothing, the personas otherwise. */}
          {preview && (
            <p className={'note sw-row' + (preview.roles.length > 0 && !preview.refused ? ' ok' : ' warn')} role="status">
              {preview.refused ? (
                <Status tone="crit">Refused</Status>
              ) : preview.roles.length > 0 ? (
                <Status tone="ok">Signs in</Status>
              ) : (
                <Status tone="warn">No access</Status>
              )}
              <span>
                {preview.refused
                  ? preview.message
                  : preview.roles.length > 0
                    ? 'Would sign in as ' + preview.personas.map(nameOf).join(', ')
                    : preview.message}
              </span>
            </p>
          )}
        </div>
      )}
    </section>
  )
}
